/**
 * W-31c step 2 — file column → template field, and the locale normalising that
 * turns a Turkish spreadsheet cell into text `private.import_num` /
 * `private.import_date` read the same way the owner meant it.
 *
 * The server is the authority on what a value *means*; this file only removes
 * the ambiguity the server cannot see. "1,234.5" reaches `import_num` as
 * 1,2345 because it strips dots before swapping the comma — so a dot-decimal
 * file has to be normalised here, on the client, where the whole column is
 * visible and the separator can be voted on.
 */
import { trFold } from '../text';
import type { ImportField } from './fields';

/**
 * §8.3 header matching: drop the " *", collapse spaces, fold Turkish letters.
 * `trFold` is the mirror of `private.tr_fold`, so a header folded here matches
 * the way the SQL side folds the same text.
 */
export function foldHeader(header: string): string {
  return trFold(header.replace(/\*+\s*$/, ''))
    .replace(/[()[\]{}.,;:_/\\-]+/g, ' ')
    .replace(/²/g, '2')
    .replace(/\s+/g, ' ')
    .trim();
}

/** `null` = "Yok say": the column is read but not sent. */
export type ColumnMapping = (string | null)[];

/**
 * Best-effort automatic mapping: an exact folded header wins, then an alias,
 * then nothing. A field is used at most once — the first column that claims it
 * keeps it, which is what the design's "A field can be mapped once" means.
 */
export function autoMapColumns(headers: string[], fields: ImportField[]): ColumnMapping {
  const byFolded = new Map<string, ImportField>();
  for (const f of fields) {
    byFolded.set(foldHeader(f.header), f);
    for (const a of f.aliases) if (!byFolded.has(a)) byFolded.set(a, f);
  }
  const taken = new Set<string>();
  return headers.map((h) => {
    const hit = byFolded.get(foldHeader(h));
    if (!hit || taken.has(hit.key)) return null;
    taken.add(hit.key);
    return hit.key;
  });
}

/** Choosing a field for one column unmaps whichever column held it before. */
export function setColumnField(mapping: ColumnMapping, index: number, key: string | null): ColumnMapping {
  return mapping.map((cur, i) => (i === index ? key : key !== null && cur === key ? null : cur));
}

export function mappedKeys(mapping: ColumnMapping): string[] {
  return mapping.filter((k): k is string => k !== null);
}

export function missingRequired(mapping: ColumnMapping, fields: ImportField[]): ImportField[] {
  const mapped = new Set(mappedKeys(mapping));
  return fields.filter((f) => f.required && !mapped.has(f.key));
}

/**
 * The file is the template itself when every required column is present under
 * its own header and no column is left unmatched — then step 2 is skipped and
 * the footer reads "Şablon eksiksiz" (design W-31c board 1).
 */
export function isExactTemplate(headers: string[], fields: ImportField[]): boolean {
  if (headers.length === 0) return false;
  const wanted = new Set(fields.map((f) => foldHeader(f.header)));
  const seen = headers.map(foldHeader);
  if (seen.some((h) => !wanted.has(h))) return false;
  return fields.filter((f) => f.required).every((f) => seen.includes(foldHeader(f.header)));
}

/** First distinct non-empty values of one column — the "Örnek değerler" cell. */
export function columnSamples(rows: string[][], index: number, max = 3): string[] {
  const out: string[] = [];
  for (const row of rows) {
    const v = row[index];
    if (!v || out.includes(v)) continue;
    out.push(v);
    if (out.length >= max) break;
  }
  return out;
}

// ── numbers ─────────────────────────────────────────────────────────────────
export type DecimalSeparator = 'comma' | 'dot';

const MINUS_SIGNS = /[−–—]/g;
const SPACES = /[\s\u00a0\u202f]/g;

/**
 * "420 koli" → { text: "420", unit: "koli" } (§8.3: a trailing unit token is
 * accepted and fills an empty "Birim"). Anything that is not
 * "<number><space><word>" comes back unchanged with no unit.
 */
export function splitQtyUnit(value: string): { text: string; unit: string | null } {
  const m = /^([+-−–—]?[\d.,\s\u00a0]+?)\s+([^\d\s][^\d]*)$/.exec(value.trim());
  if (!m) return { text: value.trim(), unit: null };
  return { text: m[1]!.trim(), unit: m[2]!.trim() };
}

/**
 * Canonical plain number text: "-1234.5". Both `private.import_num` and
 * `Number()` read it the same way, so nothing downstream has to guess again.
 * Text that is not a number at all is returned trimmed, so the server reports
 * the proper row error instead of the client silently dropping the cell.
 */
export function normalizeNumberText(value: string, sep: DecimalSeparator): string {
  let v = value.trim().replace(SPACES, '').replace(MINUS_SIGNS, '-');
  if (v === '') return '';
  if (v.startsWith('+')) v = v.slice(1);
  v = sep === 'comma' ? v.replace(/\./g, '').replace(/,/g, '.') : v.replace(/,/g, '');
  return /^-?\d+(\.\d+)?$/.test(v) ? v : value.trim();
}

/**
 * Which separator this file uses for decimals. A value carrying both takes the
 * later one; a value with a single separator votes only when the group after it
 * is not the three digits of a thousands group. Ties stay Turkish.
 */
export function detectDecimalSeparator(values: Iterable<string>): DecimalSeparator {
  let comma = 0;
  let dot = 0;
  for (const raw of values) {
    const v = raw.trim().replace(SPACES, '');
    if (v === '') continue;
    const lastComma = v.lastIndexOf(',');
    const lastDot = v.lastIndexOf('.');
    if (lastComma >= 0 && lastDot >= 0) {
      if (lastComma > lastDot) comma += 1;
      else dot += 1;
      continue;
    }
    const at = lastComma >= 0 ? lastComma : lastDot;
    if (at < 0) continue;
    const tail = v.slice(at + 1);
    if (!/^\d+$/.test(tail) || tail.length === 3) continue; // "1.234" — a thousands group
    if (lastComma >= 0) comma += 1;
    else dot += 1;
  }
  return dot > comma ? 'dot' : 'comma';
}

// ── dates ───────────────────────────────────────────────────────────────────
export const DATE_FORMATS = ['dd.MM.yyyy', 'yyyy-MM-dd', 'dd/MM/yyyy'] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];

/**
 * Normalises to "DD.MM.YYYY", the first shape `private.import_date` matches.
 * Two-digit years are refused (§8.3) by being passed through untouched, which
 * makes the server report the row instead of guessing a century.
 */
export function normalizeDateText(value: string, fmt: DateFormat): string {
  const v = value.trim();
  if (v === '') return '';
  const parts = v.split(/[./-]/).map((p) => p.trim());
  if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) return v;
  const [a, b, c] = parts as [string, string, string];
  const [d, m, y] = fmt === 'yyyy-MM-dd' ? [c, b, a] : [a, b, c];
  if (y.length !== 4) return v;
  const day = Number(d);
  const month = Number(m);
  if (day < 1 || day > 31 || month < 1 || month > 12) return v;
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${y}`;
}

// ── row → raw ───────────────────────────────────────────────────────────────
/**
 * The template columns that name a unit. The validators compare them against
 * `units.code` / `item_uoms.unit`, i.e. `pcs` / `case`, while the template and
 * every screen speak Turkish ("adet", "koli") — the opening-stock column note
 * literally reads "adet, paket, koli, kg, m, bobin, palet…". Worse,
 * `splitQtyUnit` pulls a unit out of "420 koli" on its own, so the pipeline
 * produced a name even when the owner typed none. Resolving here is in keeping
 * with the rest of this module: `import_rows.raw` holds the canonical form, and
 * numbers and dates are already canonicalised the same way.
 */
const UNIT_KEYS = new Set(['unit', 'base_unit', 'stock_unit']);

/** A row of `units` as the wizard already has it from `useUnits()`. */
export interface UnitChoice {
  code: string;
  name_tr?: string | null;
  name_en?: string | null;
}

/**
 * A code, a Turkish name or an English name → the code. An unknown value comes
 * back unchanged so the server still reports the owner's actual typo instead of
 * the client quietly dropping it. A plural "koliler"-style suffix is not guessed:
 * only what the company itself has named.
 */
export function resolveUnitCode(value: string, units: UnitChoice[] | undefined): string {
  const raw = value.trim();
  if (!raw || !units?.length) return raw;
  const folded = foldHeader(raw);
  for (const u of units) if (foldHeader(u.code) === folded) return u.code;
  for (const u of units) if (u.name_tr && foldHeader(u.name_tr) === folded) return u.code;
  for (const u of units) if (u.name_en && foldHeader(u.name_en) === folded) return u.code;
  return raw;
}

export interface RawRowOptions {
  decimal: DecimalSeparator;
  date: DateFormat;
  /** The company's units, so a Turkish unit name becomes its code. */
  units?: UnitChoice[];
}

/**
 * One `import_rows.raw` object. Empty cells are left out — the validators read
 * every key through `nullif(btrim(...), '')`, so an absent key and an empty
 * string mean the same thing and the smaller payload leaves room in the 500-row
 * chunk.
 */
export function rowToRaw(
  cells: string[],
  mapping: ColumnMapping,
  fields: ImportField[],
  opts: RawRowOptions,
): Record<string, string> {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const raw: Record<string, string> = {};
  let unitFromQty: string | null = null;
  mapping.forEach((key, i) => {
    if (!key) return;
    const field = byKey.get(key);
    const cell = (cells[i] ?? '').trim();
    if (!field || cell === '') return;
    if (field.type === 'number' || field.type === 'money') {
      const { text, unit } = splitQtyUnit(cell);
      if (unit && field.key === 'qty') unitFromQty = unit;
      raw[key] = normalizeNumberText(text, opts.decimal);
      return;
    }
    if (UNIT_KEYS.has(key)) {
      raw[key] = resolveUnitCode(cell, opts.units);
      return;
    }
    raw[key] = field.type === 'date' ? normalizeDateText(cell, opts.date) : cell;
  });
  if (unitFromQty && !raw.unit && byKey.has('unit')) raw.unit = resolveUnitCode(unitFromQty, opts.units);
  return raw;
}

/** `append_import_rows` takes 1–500 rows per call (migration 0005). */
export const IMPORT_CHUNK_SIZE = 500;

export function chunkRows<T>(rows: T[], size = IMPORT_CHUNK_SIZE): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < rows.length; i += size) out.push(rows.slice(i, i + size));
  return out;
}

/**
 * The payload of one `append_import_rows` element. `row_no` is the line the
 * owner sees in the spreadsheet — the header is row 1, so the first data row is
 * 2 and every message points at a line they can jump to.
 */
export interface StagedRow {
  row_no: number;
  raw: Record<string, string>;
}

export function stageRows(
  rows: string[][],
  mapping: ColumnMapping,
  fields: ImportField[],
  opts: RawRowOptions,
  firstRowNo = 2,
): StagedRow[] {
  return rows.map((cells, i) => ({ row_no: firstRowNo + i, raw: rowToRaw(cells, mapping, fields, opts) }));
}
